import React, { useState } from "react";
import Button from "components/button";
import Icon from "components/icon";
import Input from "components/input";
import Select from "components/select";
import Typography from "components/typography";
import {
  DriverDocumentType,
  DriverFormValues,
  LICENSE_TYPES,
  UploadedDriverDocument,
  DriverDocumentErrors,
} from "_interfaces/driver.interfaces";
import { NamespaceKeys } from "locale/types";
import { Controller, UseFormReturn } from "react-hook-form";
import DriverAccordion from "./driver-accordion";
import DatePicker from "components/date-picker";

interface DriverFinancialDocumentsAccordionProps {
  isOpen: boolean;
  onToggle: () => void;
  onNext: () => void;
  form: UseFormReturn<DriverFormValues>;
  t: (key: NamespaceKeys<"driver">, options?: Record<string, unknown>) => string;
  documentFiles: Record<DriverDocumentType, UploadedDriverDocument>;
  onDocumentFileChange: (type: DriverDocumentType, file?: File) => Promise<void> | void;
  expiryDates: Record<DriverDocumentType, string>;
  onExpiryDateChange: (type: DriverDocumentType, date: string) => void;
  documentErrors?: DriverDocumentErrors;
}

const DriverFinancialDocumentsAccordion = ({
  isOpen,
  onToggle,
  onNext,
  form,
  t,
  documentFiles,
  onDocumentFileChange,
  expiryDates,
  onExpiryDateChange,
  documentErrors,
}: DriverFinancialDocumentsAccordionProps) => {
  const {
    register,
    formState: { errors },
  } = form;

  return (
    <DriverAccordion
      title={t("accordion-financial-documents")}
      subtitle={t("accordion-financial-documents-subtitle")}
      iconName="document-text"
      isOpen={isOpen}
      onToggle={onToggle}
    >
      <div className="grid grid-cols-3 gap-4">
        <Input
          label={t("label-bank-name")}
          required
          placeholder={t("placeholder-bank-name")}
          data-testid="input-bank-name"
          {...register("bankName")}
          error={errors.bankName}
        />
        <Input
          label={t("label-bank-account")}
          required
          placeholder={t("placeholder-bank-account")}
          data-testid="input-bank-account"
          {...register("bankAccountNumber")}
          error={errors.bankAccountNumber}
        />
        <Input
          label={t("label-account-holder")}
          required
          placeholder={t("placeholder-account-holder")}
          data-testid="input-account-holder"
          {...register("bankAccountHolder")}
          error={errors.bankAccountHolder}
        />

        <Controller
          name="licenseType"
          control={form.control}
          render={({ field, fieldState }) => (
            <Select
              label={t("label-license-type")}
              required
              data-testid="select-license-type"
              options={LICENSE_TYPES}
              value={field.value}
              onValueChange={(value) => field.onChange(value)}
              error={fieldState.error}
              placeholder={t("placeholder-select")}
            />
          )}
        />
        <Input
          label={t("label-license-number")}
          required
          placeholder={t("placeholder-license-number")}
          data-testid="input-license-number"
          {...register("licenseNumber")}
          error={errors.licenseNumber}
        />
        <Controller
          name="licenseExpiryDate"
          control={form.control}
          render={({ field, fieldState }) => (
            <DatePicker
              label={t("label-license-expiry")}
              required
              placeholder="dd / mm / yyyy"
              value={field.value}
              onChange={field.onChange}
              error={fieldState.error}
            />
          )}
        />
      </div>

      <div className="mt-6 border-t border-border pt-5 grid grid-cols-3 gap-4">
        {(["skck", "ktp", "kk", "sim", "domicile_letter"] as DriverDocumentType[]).map((type) => (
          <div key={type} className="col-span-3 grid grid-cols-3 gap-4 items-start">
            <Select
              label={t("label-document-name")}
              options={[
                { label: "SKCK", value: "skck" },
                { label: "KTP", value: "ktp" },
                { label: "KK", value: "kk" },
                { label: "SIM", value: "sim" },
                { label: t("label-document-domicile") || "Domicile Letter", value: "domicile_letter" },
              ]}
              value={type}
              disabled
            />
            <DatePicker
              label={t("label-expired-date")}
              placeholder="dd / mm / yyyy"
              value={expiryDates[type] || ""}
              onChange={(date) => onExpiryDateChange(type, date)}
            />
            <div className="flex flex-col gap-1">
              <label className="flex items-center gap-1 text-sm text-text-primary">
                {t("label-upload-document")}
              </label>
              <label
                htmlFor={`upload-doc-${type}`}
                className={`min-h-[38px] rounded border px-3 py-[6px] text-sm flex items-center justify-between cursor-pointer transition-colors gap-2 ${
                  documentErrors?.[type]
                    ? "border-error shadow-[0_0_0_3px] shadow-error-500/15 bg-white"
                    : "border-border bg-white hover:bg-whiteScale-95"
                }`}
              >
                <span className="truncate text-tertiary min-w-0">
                  {documentFiles[type].fileName || t("placeholder-upload-document")}
                </span>
                <span className="inline-flex items-center gap-1 rounded bg-primary-500 px-2 py-1 text-white text-xs font-medium shrink-0 whitespace-nowrap">
                  <Icon name="document-upload" size={12} />
                  {t("btn-upload-file")}
                </span>
              </label>
              <input
                id={`upload-doc-${type}`}
                data-testid={`upload-doc-${type}`}
                type="file"
                accept=".jpg,.jpeg,.png,.pdf"
                className="hidden"
                onChange={(event) => {
                  onDocumentFileChange(type, event.target.files?.[0]);
                  event.target.value = "";
                }}
              />
              {documentErrors?.[type] ? (
                <Typography variant="bodySmall" className="text-xs text-error">
                  {documentErrors[type]}
                </Typography>
              ) : (
                <Typography variant="bodySmall" tone="muted">
                  {t("upload-document-helper")}
                </Typography>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-end pt-4">
        <Button type="button" variant="outline" color="tertiary" onClick={onNext}>
          {t("btn-next")}
        </Button>
      </div>
    </DriverAccordion>
  );
};

export default DriverFinancialDocumentsAccordion;
